<template>
  <div ref="rootEl" class="lazy-iframe-video" :class="{ '-active': isPlaying }">
    <div class="lazy-iframe-video__screen">
      <div
        v-if="isLoaded"
        class="lazy-iframe-video__player"
        :class="{ '-visible': isPlaying }"
      >
        <iframe
          ref="iframeEl"
          :src="src"
          :title="titleText"
          width="100%"
          height="100%"
          frameborder="0"
          allow="clipboard-write; autoplay; fullscreen"
          webkitallowfullscreen
          mozallowfullscreen
        />
      </div>
      <button
        v-show="!isPlaying"
        type="button"
        class="lazy-iframe-video__poster-btn"
        :aria-label="titleText ? `Смотреть видео: ${titleText}` : 'Смотреть видео'"
        @click="play"
      >
        <img
          v-if="posterSrc"
          class="lazy-iframe-video__poster"
          :src="posterSrc"
          alt=""
          :loading="priority ? 'eager' : 'lazy'"
          :fetchpriority="priority ? 'high' : undefined"
          :decoding="priority ? undefined : 'async'"
        />
        <span v-if="duration" class="lazy-iframe-video__duration t1">
          Длительность:<br /><span v-html="duration" />
        </span>
        <span class="lazy-iframe-video__play" aria-hidden="true">
          <svg viewBox="0 0 82 90" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 83.2407V6.75927C0 3.71819 3.25929 1.78941 5.92503 3.25296L75.5778 41.4937C78.3444 43.0126 78.3444 46.9874 75.5778 48.5063L5.92504 86.747C3.25929 88.2106 0 86.2818 0 83.2407Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>
    </div>
    <div v-if="title || description" class="lazy-iframe-video__meta">
      <span v-if="title" class="lazy-iframe-video__title h3" v-html="title" />
      <span
        v-if="description"
        class="lazy-iframe-video__description t1"
        v-html="description"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { LazyIframeVideoProps } from './types';
  import { useLazyVideoPlayer } from './useLazyVideoPlayer';
  import { useRutubePoster } from './useRutubePoster';

  const props = withDefaults(defineProps<LazyIframeVideoProps>(), {
    title: '',
    description: '',
    duration: '',
    poster: '',
    priority: false,
  });

  // Вся логика ленивой загрузки и управления плеером — в composable.
  // ВАЖНО: вызывать строго до `await` ниже. Composable регистрирует onMounted,
  // а `experimental.asyncContext` в проекте не включён — после await Vue уже
  // не знает текущий инстанс, и хук молча не навесится (на SSR — падение).
  const { rootEl, iframeEl, isLoaded, isPlaying, play } = useLazyVideoPlayer(
    () => props.src,
  );

  // Постер редактор задаёт `data-poster` в CMS и почти никогда не задаёт.
  // Недостающий добираем из Rutube — запрос идёт на SSR, чтобы постер попал в
  // HTML первого экрана (иначе он не станет LCP-кандидатом). Есть свой — за
  // Rutube не ходим. Название и длительность из Rutube НЕ берём: они уже
  // впечатаны в кадр постера, своя подпись поверх дала бы двойной заголовок.
  const rutubePoster = props.poster ? '' : await useRutubePoster(props.src);

  // title приходит из data-title и может содержать HTML-теги (используются
  // в подписи через v-html) — для атрибутов title/aria-label теги не нужны.
  const titleText = computed(() => props.title.replace(/<[^>]*>/g, ''));

  const posterSrc = computed(() => {
    const poster = props.poster || rutubePoster;
    if (!poster) return '';
    if (/^(https?:)?\//.test(poster)) return poster;
    return `/images/video-posters/${poster}`;
  });
</script>

<style scoped lang="scss">
  // Карточка-фасад плеера по макету ATON DS UI (Block-video-6c):
  // постер с длительностью, кнопкой play и подписью спикера. На десктопе
  // подпись лежит поверх постера, на мобильном — под ним.
  .lazy-iframe-video {
    position: relative;

    @include until($md) {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &__screen {
      position: relative;
      width: 100%;
      aspect-ratio: 884 / 497;
      overflow: hidden;
      border-radius: 16px;
      background-color: $black100;
      color: $white100;

      @include until($xl) {
        aspect-ratio: 736 / 413;
      }

      @include until($lg) {
        aspect-ratio: 590 / 331;
      }

      @include until($md) {
        aspect-ratio: 353 / 198;
        border-radius: 8px;
      }
    }

    &__player {
      position: absolute;
      inset: 0;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;

      &.-visible {
        opacity: 1;
        pointer-events: auto;
      }

      iframe {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
      }
    }

    &__poster-btn {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 32px;
      border: 0;
      cursor: pointer;

      @include until($xl) {
        padding: 24px;
      }
    }

    &__poster {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
    }

    // Цвета текстов компонента задаём компаундом с блоком (`& &__x`,
    // специфичность 0,3,0), чтобы перебить глобальные `.html-editor .t1`/`.h3`
    // (0,3,0 > 0,2,0) — компонент рендерится внутри `.html-editor` (CmsContent).
    & &__duration {
      position: absolute;
      top: 32px;
      left: 32px;
      max-width: 279px;
      color: $white70;
      text-align: left;

      @include until($xl) {
        top: 24px;
        left: 24px;
      }

      @include until($md) {
        display: none;
      }
    }

    &__play {
      position: relative;
      width: 82px;
      height: 90px;

      @include until($xl) {
        width: 64px;
        height: 70px;
      }

      @include until($lg) {
        width: 47px;
        height: 52px;
      }

      svg {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    &__meta {
      position: absolute;
      right: 32px;
      bottom: 32px;
      left: 32px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: none;

      @include until($xl) {
        right: 24px;
        bottom: 24px;
        left: 24px;
      }

      @include until($md) {
        position: static;
        gap: 16px;
        pointer-events: auto;
      }
    }

    // Пока играет видео, оверлей с подписью поверх плеера не нужен;
    // на мобильном подпись стоит под плеером — оставляем.
    &.-active &__meta {
      display: none;

      @include until($md) {
        display: flex;
      }
    }

    & &__title {
      color: $light;

      @include until($md) {
        color: $black100;
      }
    }

    & &__description {
      color: $light;

      @include until($md) {
        color: $black70;
      }
    }
  }
</style>
